import { useState } from "react";
import { deriveStatus, formatTimeRange } from "../../utils/openHouseHelpers";
import moment from "moment";

const STATUS_PILL = {
  Scheduled: "bg-blue-50 text-blue-700",
  Completed: "bg-emerald-50 text-emerald-700",
  Cancelled: "bg-slate-100 text-slate-500",
};

export default function OpenHousesTable({ rows, onAddFeedback, onEditFeedback, onEdit, onCancel, onSendRecap, onResendInvite, onDelete, onCopyLink, onSendInvites }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-10 text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
        No open houses match this filter.
      </div>
    );
  }
  return (
    <div className="border border-slate-200 rounded-lg bg-white">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-4 py-2.5 font-semibold">Property</th>
            <th className="text-left px-4 py-2.5 font-semibold">Date &amp; Time</th>
            <th className="text-left px-4 py-2.5 font-semibold">Hosting Agent</th>
            <th className="text-left px-4 py-2.5 font-semibold">Status</th>
            <th className="text-left px-4 py-2.5 font-semibold">Feedback</th>
            <th className="text-right px-4 py-2.5 font-semibold" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((oh) => (
            <OpenHouseRow
              key={oh.id}
              oh={oh}
              onAddFeedback={onAddFeedback}
              onEditFeedback={onEditFeedback}
              onEdit={onEdit}
              onCancel={onCancel}
              onSendRecap={onSendRecap}
              onResendInvite={onResendInvite}
              onDelete={onDelete}
              onCopyLink={onCopyLink}
              onSendInvites={onSendInvites}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OpenHouseRow({ oh, onAddFeedback, onEditFeedback, onEdit, onCancel, onSendRecap, onResendInvite, onDelete, onCopyLink, onSendInvites }) {
  const status = deriveStatus(oh);
  // Occupied-home flow: the OH is saved but seller/host notifications are
  // on hold until the user confirms the seller verbally OK'd the time.
  const awaitingSeller =
    !!oh.pendingSellerApproval && status === "Scheduled";
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60">
      <td className="px-4 py-3">
        <div className="font-semibold text-slate-900">
          {(oh.propertyAddress || "").split(",")[0] || "—"}
        </div>
        <div className="text-xs text-slate-500">{oh.sellerName || ""}</div>
      </td>
      <td className="px-4 py-3">
        <div className="text-slate-900">
          {oh.date ? moment(oh.date).format("ddd MMM D, YYYY") : "—"}
        </div>
        <div className="text-xs text-slate-500">
          {formatTimeRange(oh.startTime, oh.endTime)}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="text-slate-900">{oh.hostingAgentName || "—"}</div>
        <div className="text-xs text-slate-500">{oh.hostingAgentEmail || ""}</div>
      </td>
      <td className="px-4 py-3">
        {awaitingSeller ? (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800">
            Awaiting Seller OK
          </span>
        ) : (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_PILL[status]}`}>
            {status}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-slate-700 text-xs">
        {oh.feedback ? (
          <>
            <span className="font-semibold">{oh.feedback.groups || 0}</span> groups ·{" "}
            <span className="font-semibold">{oh.feedback.secondShowings || 0}</span> 2nd showings
          </>
        ) : (
          <span className="text-slate-400 italic">—</span>
        )}
      </td>
      <td className="px-4 py-3 relative">
        <div className="flex items-center justify-end gap-1 flex-nowrap">
          {awaitingSeller && (
            <button
              onClick={() => onSendInvites && onSendInvites(oh)}
              title="Send open-house notifications to the seller and hosting agent"
              className="text-amber-800 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-xs font-semibold px-2.5 py-1 rounded-md whitespace-nowrap"
            >
              Send Now
            </button>
          )}
          {status === "Scheduled" && !awaitingSeller && (
            <button
              onClick={() => onAddFeedback(oh)}
              className="text-slate-800 border border-slate-300 hover:bg-slate-100 text-xs font-semibold px-2.5 py-1 rounded-md whitespace-nowrap"
            >
              Add Feedback
            </button>
          )}
          {status === "Completed" && (
            <button
              onClick={() => onSendRecap(oh)}
              className={`text-xs font-semibold px-2.5 py-1 rounded-md whitespace-nowrap ${
                oh.recapSentAt
                  ? "text-slate-700 border border-slate-200 hover:bg-slate-50"
                  : "text-slate-800 border border-slate-300 hover:bg-slate-100"
              }`}
            >
              {oh.recapSentAt ? "Re-send Recap" : "Send Recap"}
            </button>
          )}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="text-slate-500 hover:text-slate-700 px-1.5 py-1 rounded-md shrink-0"
            aria-label="More actions"
          >
            ⋮
          </button>
        </div>
        {menuOpen && (
          <div
            className="absolute right-4 top-10 bg-white border border-slate-200 rounded-lg shadow-lg z-10 text-xs min-w-[200px]"
            onMouseLeave={() => setMenuOpen(false)}
          >
            {status === "Scheduled" && (
              <>
                <button
                  onClick={() => { setMenuOpen(false); onEdit(oh); }}
                  className="block w-full text-left px-3 py-2 hover:bg-slate-50"
                >
                  Edit
                </button>
                <button
                  onClick={() => { setMenuOpen(false); onCopyLink && onCopyLink(oh); }}
                  className="block w-full text-left px-3 py-2 hover:bg-slate-50"
                >
                  Copy public feedback link
                </button>
                <button
                  onClick={() => { setMenuOpen(false); onResendInvite(oh); }}
                  className="block w-full text-left px-3 py-2 hover:bg-slate-50"
                >
                  Re-send invite to hosting agent
                </button>
                <button
                  onClick={() => { setMenuOpen(false); onCancel(oh); }}
                  className="block w-full text-left px-3 py-2 hover:bg-red-50 text-red-600"
                >
                  Cancel
                </button>
              </>
            )}
            {status === "Completed" && (
              <button
                onClick={() => { setMenuOpen(false); onEditFeedback(oh); }}
                className="block w-full text-left px-3 py-2 hover:bg-slate-50"
              >
                Edit feedback
              </button>
            )}
            <button
              onClick={() => { setMenuOpen(false); onDelete && onDelete(oh); }}
              className="block w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 border-t border-slate-100"
            >
              Delete permanently
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}
